"use client";

import { ConstructionAnimationProvider } from "@gettactic/map-builder/src/components/map/animation/ConstructionAnimationContext";
import { EditorShortcutProvider } from "@gettactic/map-builder/src/components/map/editor/shortcuts/EditorShortcutContext/EditorShortcutContext";
import { shortcuts } from "@gettactic/map-builder/src/components/map/editor/shortcuts/shortcuts";
import type { MapBuilderProps } from "@gettactic/map-builder/src/MapBuilder";
import React from "react";
import {
  createOverlayChannel,
  OverlayChannelProvider
} from "../render/overlayChannel";
import { RenderOptionsProvider } from "../render/RenderOptionsContext";
import {
  createEditorStore,
  type EditorStore
} from "../store/createEditorStore";
import {
  EditorStoreProvider,
  useEditorSelector
} from "../store/EditorStoreContext";
import { createToolRegistry } from "../tools/registry";
import { EditorChrome } from "./EditorChrome";
import { LegacyContextBridge } from "./LegacyContextBridge";

/** Same props as the Konva `MapBuilder`, so the host switches with no
 *  prop changes. */
export type MapEditorProps = MapBuilderProps & {
  /** Exposes the store to the host (tests, tooling). */
  storeRef?: React.RefObject<EditorStore | null>;
};

/**
 * Entry point of the SVG map editor. Owns the store, wraps the existing
 * editor chrome through the legacy-context bridge, and renders the SVG
 * canvas in place of the Konva stage.
 */
export const MapEditor: React.FC<MapEditorProps> = ({ storeRef, ...props }) => {
  const [store] = React.useState(() =>
    createEditorStore(props.value, {
      initialTool:
        props.initialActiveTool ??
        (props.initialZoneToolTag ? "zone" : "select"),
      initialZoneToolTag: props.initialZoneToolTag,
      initialUi: props.initialShowZones ? { showZones: true } : undefined,
      // The map page hands over where it was looking, as the Konva editor
      // restores it; without one the canvas fits the floor on mount.
      initialViewport: props.initialPosition
        ? {
            x: props.initialPosition.x || 0,
            y: props.initialPosition.y || 0,
            scale: props.initialPosition.scaleX || 1
          }
        : undefined
    })
  );
  const [overlay] = React.useState(createOverlayChannel);
  const propsRef = React.useRef(props);
  propsRef.current = props;
  const [tools] = React.useState(() =>
    createToolRegistry({
      getZoneTag: () => store.getState().chrome.zoneToolTag,
      getMapIcons: () => propsRef.current.mapIcons ?? [],
      isRestrictedMode: () => !!propsRef.current.restrictedMode
    })
  );
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const svgRef = React.useRef<SVGSVGElement | null>(null);

  React.useEffect(() => {
    if (storeRef) storeRef.current = store;
  }, [store, storeRef]);

  useQuicksandFont();
  useResourceEditorSync(
    store,
    props.setEditingResources,
    props.editingResources
  );
  useUnsavedChangesGuard(store);

  return (
    <ConstructionAnimationProvider>
      <EditorStoreProvider store={store}>
        <OverlayChannelProvider value={overlay}>
          <StoreRenderOptions
            teamColors={props.teamColors}
            interactiveZones={props.interactiveZones}
          >
            <LegacyContextBridge
              {...props}
              canvasRef={containerRef}
              svgRef={svgRef}
            >
              <EditorShortcutProvider defaultShortcuts={shortcuts}>
                <EditorChrome
                  tools={tools}
                  containerRef={containerRef}
                  svgRef={svgRef}
                />
              </EditorShortcutProvider>
            </LegacyContextBridge>
          </StoreRenderOptions>
        </OverlayChannelProvider>
      </EditorStoreProvider>
    </ConstructionAnimationProvider>
  );
};

interface StoreRenderOptionsProps {
  teamColors?: Record<string, string>;
  interactiveZones?: MapEditorProps["interactiveZones"];
  children: React.ReactNode;
}

/** Zone colours follow the map's own category registry as it is edited. */
const StoreRenderOptions: React.FC<StoreRenderOptionsProps> = ({
  teamColors,
  interactiveZones,
  children
}) => {
  const zoneTypes = useEditorSelector((state) => state.meta.zoneTypes);
  const value = React.useMemo(
    () => ({ zoneTypes: zoneTypes ?? [], teamColors, interactiveZones }),
    [zoneTypes, teamColors, interactiveZones]
  );
  return (
    <RenderOptionsProvider value={value}>{children}</RenderOptionsProvider>
  );
};

/** Same selection → resource editor bridge as the Konva provider: while
 *  the drawer is open, it lists the resources of the selected spots in
 *  selection order. */
const useResourceEditorSync = (
  store: EditorStore,
  setEditingResources: MapEditorProps["setEditingResources"],
  editingResources: MapEditorProps["editingResources"]
) => {
  const isOpen = editingResources?.isOpen ?? false;
  React.useEffect(() => {
    if (!isOpen) return;
    let previous = store.getState().selection;
    return store.subscribe((state) => {
      if (state.selection === previous) return;
      previous = state.selection;
      const resourceIds: string[] = [];
      for (const id of state.selection) {
        const shape = state.shapes[id];
        if (shape?.type === "spot" && shape.resource_id)
          resourceIds.push(shape.resource_id);
      }
      setEditingResources({ isOpen: true, resourceIds });
    });
  }, [store, isOpen, setEditingResources]);
};

const useUnsavedChangesGuard = (store: EditorStore) => {
  React.useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!store.getState().isDirty) return;
      event.preventDefault();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [store]);
};

const useQuicksandFont = () => {
  React.useEffect(() => {
    const href =
      "https://fonts.googleapis.com/css2?family=Quicksand:wght@300;400;700&display=swap";
    if (document.querySelector(`link[href="${href}"]`)) return;
    const link = document.createElement("link");
    link.href = href;
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }, []);
};
